import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { MainLayout } from '@components/layout/MainLayout';
import { ScrollToTop } from '@components/common';

// Eager load ALL pages for prerenderer SEO
import HomePage from '@pages/HomePage';
import AboutPage from '@pages/AboutPage';
import HowWeWorkPage from '@pages/HowWeWorkPage';
import BlogPage from '@pages/BlogPage';
import BlogPostPage from '@pages/BlogPostPage';
import CareersPage from '@pages/CareersPage';
import PrivacyPolicyPage from '@pages/PrivacyPolicyPage';
import TermsOfServicePage from '@pages/TermsOfServicePage';
import WhatIsFractionalCisoPage from '@pages/WhatIsFractionalCisoPage';
import BecomeFractionalCisoPage from '@pages/BecomeFractionalCisoPage';
import KnowledgeHubPage from '@pages/KnowledgeHubPage';
import GlossaryPage from '@pages/GlossaryPage';
import CybersecurityFrameworksPage from '@pages/CybersecurityFrameworksPage';
import JoinPage from '@pages/JoinPage';
import LocationsPage from '@pages/LocationsPage';
import SectorsPage from '@pages/SectorsPage';
import ServicesPillarPage from '@pages/services';
import PackagesPillarPage from '@pages/packages';
import NotFoundPage from '@pages/NotFoundPage';

// Career pages
import FractionalCISOPage from '@pages/careers/FractionalCISOPage';
import SecurityConsultantPage from '@pages/careers/SecurityConsultantPage';
import ClientSuccessManagerPage from '@pages/careers/ClientSuccessManagerPage';

// Location pages
import LondonPage from '@pages/locations/LondonPage';
import BristolPage from '@pages/locations/BristolPage';
import BirminghamPage from '@pages/locations/BirminghamPage';
import ManchesterPage from '@pages/locations/ManchesterPage';
import CardiffPage from '@pages/locations/CardiffPage';
import EdinburghPage from '@pages/locations/EdinburghPage';
import ExeterPage from '@pages/locations/ExeterPage';
import GlasgowPage from '@pages/locations/GlasgowPage';
import BelfastPage from '@pages/locations/BelfastPage';
import LeedsPage from '@pages/locations/LeedsPage';
import LiverpoolPage from '@pages/locations/LiverpoolPage';
import CambridgePage from '@pages/locations/CambridgePage';
import OxfordPage from '@pages/locations/OxfordPage';
import BrightonPage from '@pages/locations/BrightonPage';
import NewcastlePage from '@pages/locations/NewcastlePage';
import ReadingPage from '@pages/locations/ReadingPage';

// Package detail pages
import SecurityMaturityPage from '@pages/packages/SecurityMaturityPage';
import AiGovernancePage from '@pages/packages/AiGovernancePage';
import TechnicalEstateReviewPage from '@pages/packages/TechnicalEstateReviewPage';
import MaCyberDueDiligencePage from '@pages/packages/MaCyberDueDiligencePage';
import BoardTabletopPage from '@pages/packages/BoardTabletopPage';

// Sector pages
import FinancePage from '@pages/sectors/FinancePage';
import HealthcarePage from '@pages/sectors/HealthcarePage';
import SaasPage from '@pages/sectors/SaasPage';
import AutomotivePage from '@pages/sectors/AutomotivePage';
import EnergyPage from '@pages/sectors/EnergyPage';
import LegalPage from '@pages/sectors/LegalPage';
import ManufacturingPage from '@pages/sectors/ManufacturingPage';
import RetailPage from '@pages/sectors/RetailPage';
import PrivateEquityPage from '@pages/sectors/PrivateEquityPage';

// Service detail pages
import SecurityStrategyPage from '@pages/services/SecurityStrategyPage';
import RiskAssessmentPage from '@pages/services/RiskAssessmentPage';
import CompliancePage from '@pages/services/CompliancePage';
import IncidentResponsePage from '@pages/services/IncidentResponsePage';
import SecurityCulturePage from '@pages/services/SecurityCulturePage';
import BoardReportingPage from '@pages/services/BoardReportingPage';
import CyberResiliencePage from '@pages/services/CyberResiliencePage';
import AiGovernanceServicePage from '@pages/services/AiGovernancePage';
import SupplyChainRiskPage from '@pages/services/SupplyChainRiskPage';

function BlogToNewsRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={slug ? `/news/${slug}` : '/news'} replace />;
}

function App() {
  return (
    <>
      <ScrollToTop />
      <MainLayout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPillarPage />} />
          {/* Individual service pages */}
          <Route path="/services/security-strategy" element={<SecurityStrategyPage />} />
          <Route path="/services/risk-assessment" element={<RiskAssessmentPage />} />
          <Route path="/services/compliance" element={<CompliancePage />} />
          <Route path="/services/incident-response" element={<IncidentResponsePage />} />
          <Route path="/services/security-culture" element={<SecurityCulturePage />} />
          <Route path="/services/board-reporting" element={<BoardReportingPage />} />
          <Route path="/services/cyber-resilience" element={<CyberResiliencePage />} />
          <Route path="/services/ai-governance" element={<AiGovernanceServicePage />} />
          <Route path="/services/supply-chain-risk" element={<SupplyChainRiskPage />} />
          <Route path="/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/packages" element={<PackagesPillarPage />} />
          <Route path="/packages/security-maturity-assessment" element={<SecurityMaturityPage />} />
          <Route path="/packages/ai-governance-setup" element={<AiGovernancePage />} />
          <Route path="/packages/technical-estate-review" element={<TechnicalEstateReviewPage />} />
          <Route path="/packages/ma-cyber-due-diligence" element={<MaCyberDueDiligencePage />} />
          <Route path="/packages/board-tabletop-exercises" element={<BoardTabletopPage />} />
          <Route path="/news" element={<BlogPage />} />
          <Route path="/news/:slug" element={<BlogPostPage />} />
          <Route path="/blog" element={<BlogToNewsRedirect />} />
          <Route path="/blog/:slug" element={<BlogToNewsRedirect />} />
          <Route path="/careers" element={<CareersPage />} />
          {/* Careers role pages */}
          <Route path="/careers/fractional-ciso" element={<FractionalCISOPage />} />
          <Route path="/careers/security-consultant" element={<SecurityConsultantPage />} />
          <Route path="/careers/client-success-manager" element={<ClientSuccessManagerPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/knowledge-hub" element={<KnowledgeHubPage />} />
          <Route path="/knowledge-hub/glossary" element={<GlossaryPage />} />
          <Route
            path="/knowledge-hub/cybersecurity-frameworks"
            element={<CybersecurityFrameworksPage />}
          />
          <Route path="/what-is-a-fractional-ciso" element={<WhatIsFractionalCisoPage />} />
          <Route path="/become-a-fractional-ciso" element={<BecomeFractionalCisoPage />} />
          <Route path="/join" element={<JoinPage />} />
          {/* Location pages - SEO landing pages */}
          <Route path="/gb/london" element={<LondonPage />} />
          <Route path="/gb/bristol" element={<BristolPage />} />
          <Route path="/gb/birmingham" element={<BirminghamPage />} />
          <Route path="/gb/manchester" element={<ManchesterPage />} />
          <Route path="/gb/cardiff" element={<CardiffPage />} />
          <Route path="/gb/edinburgh" element={<EdinburghPage />} />
          <Route path="/gb/exeter" element={<ExeterPage />} />
          <Route path="/gb/glasgow" element={<GlasgowPage />} />
          <Route path="/gb/belfast" element={<BelfastPage />} />
          <Route path="/gb/leeds" element={<LeedsPage />} />
          <Route path="/gb/liverpool" element={<LiverpoolPage />} />
          <Route path="/gb/cambridge" element={<CambridgePage />} />
          <Route path="/gb/oxford" element={<OxfordPage />} />
          <Route path="/gb/brighton" element={<BrightonPage />} />
          <Route path="/gb/newcastle" element={<NewcastlePage />} />
          <Route path="/gb/reading" element={<ReadingPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/sectors" element={<SectorsPage />} />
          {/* Sector pages - SEO landing pages */}
          <Route path="/sectors/finance" element={<FinancePage />} />
          <Route path="/sectors/saas" element={<SaasPage />} />
          <Route path="/sectors/automotive" element={<AutomotivePage />} />
          <Route path="/sectors/energy" element={<EnergyPage />} />
          <Route path="/sectors/healthcare" element={<HealthcarePage />} />
          <Route path="/sectors/legal" element={<LegalPage />} />
          <Route path="/sectors/manufacturing" element={<ManufacturingPage />} />
          <Route path="/sectors/retail" element={<RetailPage />} />
          <Route path="/sectors/private-equity" element={<PrivateEquityPage />} />
          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </MainLayout>
    </>
  );
}

export default App;
